// Thin wrapper around sdk-dapp's ProviderFactory. The factory wires the
// resulting DappProvider into the zustand store so useGetAccount /
// useGetIsLoggedIn react to login/logout across the dApp.

import { ProviderFactory } from "@multiversx/sdk-dapp/out/providers/ProviderFactory";
import { ProviderTypeEnum } from "@multiversx/sdk-dapp/out/providers/types/providerFactory.types";
import { logoutAction } from "@multiversx/sdk-dapp/out/store/actions/sharedActions/sharedActions";
import type { DappProvider } from "@multiversx/sdk-dapp/out/providers/DappProvider/DappProvider";

let currentProvider: DappProvider | null = null;

async function connectWith(
  type: (typeof ProviderTypeEnum)[keyof typeof ProviderTypeEnum],
  anchor?: HTMLElement,
): Promise<{ address: string } | null> {
  // NOTE: do NOT call ProviderFactory.destroy() before create. sdk-dapp's
  // initApp() already initialised the WalletConnect Core when we passed a
  // projectId; destroy() then nukes it and the next create() re-inits the
  // core ("Init() called 2 times" warning + a half-broken WC session that
  // can't be cancelled cleanly).
  currentProvider = await ProviderFactory.create({ type, anchor });
  await currentProvider.init();
  return currentProvider.login();
}

/** DeFi Wallet — Chrome/Firefox extension. Detects via window.elrondWallet. */
export const connectExtension = () => connectWith(ProviderTypeEnum.extension);

/** Ledger Nano S/X/Plus via WebHID. */
export const connectLedger = (anchor?: HTMLElement) =>
  connectWith(ProviderTypeEnum.ledger, anchor);

/** MultiversX Web Wallet (wallet.multiversx.com) via a popup. */
export const connectWebWallet = () => connectWith(ProviderTypeEnum.crossWindow);

/** xPortal mobile app via WalletConnect v2. Requires WALLET_CONNECT_PROJECT_ID
 *  in /api/config AND an `anchor` HTMLElement where sdk-dapp's web component
 *  `<mvx-wallet-connect>` will mount the QR + status UI. Without the anchor
 *  the QR never renders and cancelLogin throws "Session is not connected". */
export const connectXPortal = (anchor?: HTMLElement) =>
  connectWith(ProviderTypeEnum.walletConnect, anchor);

/** Heuristic: is the MultiversX DeFi Wallet extension injected on this page?
 *  Used to show a "Detected" badge in the picker. */
export function isExtensionDetected(): boolean {
  if (typeof window === "undefined") return false;
  return "elrondWallet" in window || "multiversxWallet" in window;
}

export function getProvider(): DappProvider | null {
  return currentProvider;
}

export async function disconnect(): Promise<void> {
  // Try to gracefully log out through the wallet extension if we still hold
  // a provider reference. After a page reload, this reference is null but the
  // sdk-dapp store may still hold the persisted login — so we ALWAYS clear
  // the store too via logoutAction().
  if (currentProvider) {
    try {
      await currentProvider.logout({ shouldBroadcastLogoutAcrossTabs: true });
    } catch {
      /* extension may have already closed; we still want to clear our state */
    }
    try {
      await ProviderFactory.destroy();
    } catch {
      /* idempotent */
    }
    currentProvider = null;
  }
  logoutAction();
}
